/**
 * Per-feature activation confirmation for `SaTenantFeatures`.
 *
 * Extracted from that component when LOCKER-408 pushed it past the ~400-line
 * limit in `.claude/rules/frontend-conventions.md`. Behaviour is unchanged
 * except that it reads the row's RESOLVED state (`isActivated`) rather than the
 * assignment's own flag, so a feature granted by `defaultActivated` confirms as
 * a deactivation instead of offering to activate what is already on.
 */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Feature } from "shared/feature"
import { FeatureAssignment } from "shared/featureAssignment"

export type SaTenantPendingToggle = {
  feature: Feature
  assignment?: FeatureAssignment
  /** The row's resolved state — what the checkbox is showing. */
  isActivated: boolean
}

type SaTenantFeatureToggleDialogProps = {
  pending: SaTenantPendingToggle | null
  isBusy: boolean
  onCancel: () => void
  onConfirm: (pending: SaTenantPendingToggle) => void
}

export const SaTenantFeatureToggleDialog: FC<
  SaTenantFeatureToggleDialogProps
> = ({ pending, isBusy, onCancel, onConfirm }) => {
  const { t } = useTranslation()
  const deactivating = !!pending?.isActivated
  const featureName = pending?.feature?.name || pending?.feature?.id

  return (
    <Dialog data-ui="UI-SL2W-136" open={!!pending} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>
        {deactivating ? t("confirmDeactivate") : t("confirmActivate")}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {deactivating
            ? t("confirmDeactivateFeature", { feature: featureName })
            : t("confirmActivateFeature", { feature: featureName })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onCancel}
          disabled={isBusy}
          variant="outlined"
          size="small"
        >
          {t("cancel")}
        </Button>
        <Button
          onClick={() => pending && onConfirm(pending)}
          disabled={isBusy}
          variant="contained"
          size="small"
          color={deactivating ? "warning" : "primary"}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SaTenantFeatureToggleDialog
